import * as Y from 'yjs';

// CanvasBinding is defined in lib/canvas (reachable by both lib/ and features/)
// and re-exported here so intra-canvas consumers keep importing it from
// canvas-roots. See lib/canvas/types/binding.ts for the layering rationale.
export type { CanvasBinding } from '@/lib/canvas/types/binding';
import type { CanvasBinding } from '@/lib/canvas/types/binding';

export const TOP_LEVEL_BINDING: CanvasBinding = { kind: 'top-level' };
export const nestedBinding = (parentKey: string): CanvasBinding => ({ kind: 'nested', parentKey });

export interface CanvasRoots {
  nodes: Y.Map<Y.Map<unknown>>;
  edges: Y.Map<Y.Map<unknown>>;
  zOrder: Y.Array<string>;
  metadata: Y.Map<unknown>;
  edgeZOrder: Y.Array<string>;
}

/**
 * Lazily create-and-integrate a nested child Y.Map under `parent[key]`.
 * `parent.get(key)` returns `undefined` until the child is set; once set, it
 * returns the live integrated type. Returns the existing child if present.
 */
function ensureMap(parent: Y.Map<unknown>, key: string): Y.Map<unknown> {
  let m = parent.get(key) as Y.Map<unknown> | undefined;
  if (!m) {
    m = new Y.Map();
    parent.set(key, m);
  }
  return m;
}

/**
 * Lazily create-and-integrate a nested child Y.Array under `parent[key]`.
 */
function ensureArray(parent: Y.Map<unknown>, key: string): Y.Array<string> {
  let a = parent.get(key) as Y.Array<string> | undefined;
  if (!a) {
    a = new Y.Array();
    parent.set(key, a);
  }
  return a;
}

/**
 * Resolve the 5 canvas root Yjs types for a doc under the given binding.
 * top-level → doc.getMap('nodes') etc. (byte-identical to the legacy inline access).
 * nested    → the roots live INSIDE doc.getMap(parentKey) as a self-contained sub-tree.
 *
 * THE single chokepoint: every canvas-root access routes through here, so the
 * storage location is configurable in exactly one place.
 */
export function canvasRoots(doc: Y.Doc, binding: CanvasBinding): CanvasRoots {
  if (binding.kind === 'nested') {
    const parent = doc.getMap<unknown>(binding.parentKey);
    return {
      // The single resolver-local cast: node/edge values are themselves Y.Maps
      // (id → node/edge Y.Map). Typing them here keeps ~40 call sites cast-free.
      nodes: ensureMap(parent, 'nodes') as Y.Map<Y.Map<unknown>>,
      edges: ensureMap(parent, 'edges') as Y.Map<Y.Map<unknown>>,
      zOrder: ensureArray(parent, 'zOrder'),
      metadata: ensureMap(parent, 'metadata'),
      edgeZOrder: ensureArray(parent, 'edgeZOrder'),
    };
  }
  return {
    nodes: doc.getMap<Y.Map<unknown>>('nodes'),
    edges: doc.getMap<Y.Map<unknown>>('edges'),
    zOrder: doc.getArray('zOrder'),
    metadata: doc.getMap('metadata'),
    edgeZOrder: doc.getArray('edgeZOrder'),
  };
}
